const SETTLED = new Set([
  "preflighting",
  "preflight_failed",
  "check_failed",
  "security_failed",
  "security_timed_out",
  "ready",
  "published",
]);

const REVIEW_AT = 0.35;
const BLOCK_AT = 0.7;

export function reportText(value) {
  if (value == null || value === false) return "";
  if (typeof value === "string") return value.trim();
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  if (typeof value === "object") {
    const message = reportText(value.message);
    if (message) {
      const code = reportText(value.code);
      return code ? `${message} (${code})` : message;
    }
    try {
      const json = JSON.stringify(value);
      return json && json !== "{}" ? json : "";
    } catch {
      return "";
    }
  }
  const text = String(value).trim();
  return text === "[object Object]" ? "" : text;
}

export function infrastructureReport(report) {
  if (!report || typeof report !== "object") return false;
  if (report.infrastructure === true || report.outcome === "crashed")
    return true;
  if (report.decision === "timed_out" || report.decision === "check_failed")
    return true;
  const block = Number(report.scores?.block);
  const humanReview =
    report.decision === "review" &&
    Number.isFinite(block) &&
    block >= REVIEW_AT &&
    block < BLOCK_AT;
  if (humanReview) return false;
  if (report.decision === "review" && reportText(report.error)) return true;
  if (
    report.decision === "review" &&
    /could not start|not configured|key is missing|did not return/i.test(
      reportText(report.summary),
    )
  ) {
    return true;
  }
  return false;
}

export function releaseCheckMessage(check, fallback) {
  const report = check?.report;
  if (!report || typeof report !== "object") return fallback;
  const summary = reportText(report.summary);
  if (summary) return summary;
  const error = reportText(report.error);
  if (error) return error;
  const failures = Array.isArray(report.failures)
    ? report.failures.map((item) => reportText(item)).filter(Boolean)
    : [];
  return failures.length ? failures.join(" ") : fallback;
}

export function ourSideDetail(check, fallback) {
  return `The check failed on our side (${releaseCheckMessage(check, fallback)}). Your plugin was not judged.`;
}

export function pluginReleaseInProgress(plugin) {
  return Boolean(
    plugin?.busy ||
    (plugin?.versions || []).some(
      (version) => version.status === "preflighting",
    ),
  );
}

export function pluginImportRows(plugin) {
  const preflighting = (plugin?.versions || []).some(
    (version) => version.status === "preflighting",
  );
  if (plugin?.last_import_error && !preflighting) {
    return [
      {
        state: "error",
        label: "Release",
        detail: String(plugin.last_import_error),
      },
    ];
  }
  if (plugin?.busy && !preflighting) {
    return [
      {
        state: "run",
        label: "Release",
        detail: "Looking for the latest release.",
      },
    ];
  }
  return [];
}

export function versionReleaseRows(version) {
  const status = version?.status || "";
  const checks = version?.checks || {};
  const files = checks.static || null;
  const sandbox = checks.dynamic || null;
  const security = checks.security || null;
  const decision = security?.report?.decision;
  const filesInfra = infrastructureReport(files?.report);
  const sandboxInfra = infrastructureReport(sandbox?.report);
  const securityInfra =
    infrastructureReport(security?.report) || status === "security_timed_out";
  const filesFailed =
    !filesInfra && (status === "static_failed" || files?.passed === false);
  const filesDone =
    !filesFailed &&
    !filesInfra &&
    (files?.passed === true ||
      Boolean(sandbox) ||
      Boolean(security) ||
      SETTLED.has(status));
  const sandboxFailed =
    !sandboxInfra &&
    (status === "preflight_failed" || sandbox?.passed === false);
  const sandboxDone =
    !sandboxFailed &&
    !sandboxInfra &&
    (sandbox?.passed === true ||
      status === "security_failed" ||
      status === "check_failed" ||
      status === "security_timed_out" ||
      status === "ready" ||
      status === "published");
  const securityBlocked =
    !securityInfra && (decision === "block" || status === "security_failed");
  const securityDone =
    !securityInfra &&
    !securityBlocked &&
    decision !== "review" &&
    (decision === "pass" ||
      security?.passed === true ||
      ((status === "ready" || status === "published") && !security));

  const rows = [];
  if (filesInfra) {
    rows.push({
      state: "error",
      ours: true,
      label: "Release files",
      detail: ourSideDetail(files, "The release files could not be read."),
    });
  } else if (filesFailed) {
    rows.push({
      state: "error",
      label: "Release files",
      detail: releaseCheckMessage(
        files,
        "The release files could not be imported.",
      ),
    });
  } else if (filesDone) {
    rows.push({
      state: "done",
      label: "Release files",
      detail: "The release files look right.",
    });
  } else {
    rows.push({
      state: "run",
      label: "Release files",
      detail: "Reading the release.",
    });
  }

  if (filesFailed || filesInfra) {
    rows.push({
      state: "wait",
      label: "Sandbox",
      detail: "Waiting until the release files pass.",
    });
  } else if (sandboxInfra) {
    rows.push({
      state: "error",
      ours: true,
      label: "Sandbox",
      detail: ourSideDetail(sandbox, "The sandbox could not run the plugin."),
    });
  } else if (sandboxFailed) {
    rows.push({
      state: "error",
      label: "Sandbox",
      detail: releaseCheckMessage(sandbox, "The sandbox check failed."),
    });
  } else if (sandboxDone) {
    rows.push({
      state: "done",
      label: "Sandbox",
      detail: "The plugin ran in the sandbox.",
    });
  } else if (status === "preflighting") {
    rows.push({
      state: "run",
      label: "Sandbox",
      detail: "Checking the plugin in a sandbox.",
    });
  } else {
    rows.push({
      state: "wait",
      label: "Sandbox",
      detail: "Waiting to start.",
    });
  }

  if (
    filesFailed ||
    filesInfra ||
    ((sandboxFailed || sandboxInfra) && !security)
  ) {
    rows.push({
      state: "wait",
      label: "Security review",
      detail: "Waiting until the earlier checks pass.",
    });
  } else if (securityInfra) {
    rows.push({
      state: "error",
      ours: true,
      label: "Security review",
      detail: ourSideDetail(
        security,
        "The security review ran for 30 minutes without a result.",
      ),
    });
  } else if (
    securityBlocked ||
    (security?.passed === false && decision !== "review")
  ) {
    rows.push({
      state: "error",
      label: "Security review",
      detail: releaseCheckMessage(security, "The security review failed."),
    });
  } else if (decision === "review") {
    rows.push({
      state: "run",
      label: "Security review",
      detail: releaseCheckMessage(
        security,
        "A person needs to look at this before it can be published.",
      ),
    });
  } else if (securityDone) {
    rows.push({
      state: "done",
      label: "Security review",
      detail: security
        ? "The security review passed."
        : "No security review was required.",
    });
  } else if (status === "preflighting") {
    rows.push({
      state: "run",
      label: "Security review",
      detail: "Reviewing the plugin source.",
    });
  } else {
    rows.push({
      state: "wait",
      label: "Security review",
      detail: "Waiting to start.",
    });
  }
  if (status === "published") {
    rows.push({
      state: "done",
      label: "Published",
      detail: "This version is in the catalog.",
      emphasis: true,
    });
  }
  return rows;
}
